import {
  getCatalog,
  loadBlock,
  type CatalogBlock,
  type LoadedBlock,
} from '@/features/shared/services/block-loader'
// Isolated Studio worktrees can share node_modules with another checkout, whose
// @cogo/schema manifest may not expose this checkout's release yet. Read the
// committed authority directly; the package also exports it for clean installs.
import baselineDesignOntologyRelease from '../../../../../../packages/cogo-schema/ui-block-source-corpus/required-library/design-ontology-releases/baseline.v1.json'

/** A browser-safe reference to one of the blocks admitted by catalog-v2. */
export interface UiBlockRef {
  category: string
  blockId: string
}

export interface UiBlockSummary extends UiBlockRef {
  id: string
  categoryLabel: string
  description: string
  tags: readonly string[]
  rendererTypes: readonly string[]
  complexity: string
}

export interface UiBlockDiscovery {
  catalogVersion: string
  admittedCount: number
  blocks: readonly UiBlockSummary[]
}

export interface UiBlockSearchOptions {
  limit?: number
  category?: string
}

export interface UiBlockRecommendationRequest {
  intent: string
  limit?: number
}

export interface ResolvedUiBlockPreview {
  ref: UiBlockRef
  summary: UiBlockSummary
  block: LoadedBlock
  designSystem: Readonly<Record<string, string>>
  designOntologyRelease: {
    releaseId: string
    contentHash: string
  }
  mode: 'read-only'
}

export interface UiBlockReplacementResult {
  available: false
  candidates: readonly never[]
  reason: 'replacement-authority-unavailable'
  explanation: string
}

export class UiBlockPreviewAuthorityError extends Error {
  constructor(
    readonly code: 'not-admitted' | 'design-authority-unresolved',
    readonly ref: UiBlockRef,
    readonly roles: readonly string[] = [],
  ) {
    super(
      code === 'not-admitted'
        ? `UI Block is not admitted by the Studio catalog: ${ref.category}/${ref.blockId}`
        : `UI Block preview design authority is unresolved: ${roles
          .map((role) => `@ds:${role}`)
          .join(', ')}`,
    )
    this.name = 'UiBlockPreviewAuthorityError'
  }
}

const INTENT_ALIASES: Readonly<Record<string, readonly string[]>> = {
  dashboard: ['dashboard', 'analytics', 'metric', 'chart', 'stats', 'admin', 'data-display'],
  '대시보드': ['dashboard', 'analytics', 'metric', 'chart', 'stats', 'admin', 'data-display'],
  store: ['ecommerce', 'marketplace', 'product', 'cart', 'checkout', 'pricing'],
  shop: ['ecommerce', 'marketplace', 'product', 'cart', 'checkout', 'pricing'],
  '스토어': ['ecommerce', 'marketplace', 'product', 'cart', 'checkout', 'pricing'],
  '쇼핑': ['ecommerce', 'marketplace', 'product', 'cart', 'checkout', 'pricing'],
  landing: ['hero', 'feature', 'cta', 'testimonial', 'pricing', 'navigation', 'footer'],
  '랜딩': ['hero', 'feature', 'cta', 'testimonial', 'pricing', 'navigation', 'footer'],
  login: ['auth', 'login', 'signup', 'oauth', 'verify'],
  auth: ['auth', 'login', 'signup', 'oauth', 'verify'],
  '로그인': ['auth', 'login', 'signup', 'oauth', 'verify'],
  'ダッシュボード': ['dashboard', 'analytics', 'metric', 'chart', 'stats', 'admin'],
  'ストア': ['ecommerce', 'marketplace', 'product', 'cart', 'checkout', 'pricing'],
  'ランディング': ['hero', 'feature', 'cta', 'testimonial', 'pricing'],
  'サインイン': ['auth', 'login', 'signup', 'oauth', 'verify'],
  '仪表板': ['dashboard', 'analytics', 'metric', 'chart', 'stats', 'admin'],
  '儀表板': ['dashboard', 'analytics', 'metric', 'chart', 'stats', 'admin'],
  '商店': ['ecommerce', 'marketplace', 'product', 'cart', 'checkout', 'pricing'],
  '着陆页': ['hero', 'feature', 'cta', 'testimonial', 'pricing'],
  '登陸頁': ['hero', 'feature', 'cta', 'testimonial', 'pricing'],
  '登录': ['auth', 'login', 'signup', 'oauth', 'verify'],
  '登入': ['auth', 'login', 'signup', 'oauth', 'verify'],
}

interface BrowserSafeDesignOntologyRelease {
  schemaVersion: 'cogo.design-ontology-release.v1'
  releaseId: string
  contentHash: string
  roles: Record<string, string>
}

const BASELINE_DESIGN_ONTOLOGY_RELEASE =
  baselineDesignOntologyRelease as BrowserSafeDesignOntologyRelease
const DESIGN_HANDLE_PATTERN = /@ds:([A-Za-z0-9._:-]+)/g

function referencedDesignRoles(value: unknown): readonly string[] {
  const roles = new Set<string>()
  const visit = (candidate: unknown): void => {
    if (typeof candidate === 'string') {
      for (const match of candidate.matchAll(DESIGN_HANDLE_PATTERN)) {
        const role = match[1]
        const suffix = candidate.slice((match.index ?? 0) + match[0].length)
        if (role.endsWith('.') && suffix.startsWith('#{')) {
          const family = Object.keys(BASELINE_DESIGN_ONTOLOGY_RELEASE.roles).filter(
            (knownRole) => knownRole.startsWith(role),
          )
          if (family.length > 0) {
            family.forEach((knownRole) => roles.add(knownRole))
            continue
          }
        }
        roles.add(role)
      }
      return
    }
    if (Array.isArray(candidate)) {
      candidate.forEach(visit)
      return
    }
    if (candidate && typeof candidate === 'object') {
      Object.values(candidate).forEach(visit)
    }
  }
  visit(value)
  return [...roles].sort()
}

function resolveBaselineDesignSystem(
  block: LoadedBlock,
  ref: UiBlockRef,
): Readonly<Record<string, string>> {
  const roles = referencedDesignRoles(block.ui)
  const unresolved = roles.filter((role) => {
    const className = BASELINE_DESIGN_ONTOLOGY_RELEASE.roles[role]
    return typeof className !== 'string' || className.trim().length === 0
  })
  if (unresolved.length > 0) {
    throw new UiBlockPreviewAuthorityError('design-authority-unresolved', ref, unresolved)
  }

  return Object.fromEntries(
    roles.map((role) => [role, BASELINE_DESIGN_ONTOLOGY_RELEASE.roles[role]]),
  )
}

function normalize(value: string): string {
  return value.trim().toLocaleLowerCase()
}

function tokenize(value: string): string[] {
  const normalized = normalize(value)
  const base = normalized.split(/[^\p{L}\p{N}-]+/u).filter(Boolean)
  const expanded = base.flatMap((token) =>
    Object.entries(INTENT_ALIASES).flatMap(([alias, terms]) =>
      token.includes(alias) || (token.length >= 2 && alias.includes(token)) ? terms : [],
    ),
  )
  return [...new Set([...base, ...expanded])]
}

function toSummary(block: CatalogBlock, categoryLabel: string): UiBlockSummary {
  return {
    id: block.id,
    blockId: block.id,
    category: block.category,
    categoryLabel,
    description: block.description,
    tags: block.tags,
    rendererTypes: block.rendererTypes,
    complexity: block.complexity,
  }
}

function admittedBlocks(): UiBlockDiscovery {
  const catalog = getCatalog()
  const blocks = catalog.categories.flatMap((category) =>
    category.blocks.map((block) => toSummary(block, category.label)),
  )

  return {
    catalogVersion: catalog.version,
    admittedCount: blocks.length,
    blocks,
  }
}

/** Lists only the entries admitted by the browser-safe catalog projection. */
export function discover(options: Pick<UiBlockSearchOptions, 'category'> = {}): UiBlockDiscovery {
  const discovery = admittedBlocks()
  if (!options.category) return discovery

  const blocks = discovery.blocks.filter((block) => block.category === options.category)
  return { ...discovery, blocks }
}

/** Searches the admitted projection. Files on disk but absent from the catalog never appear. */
export function search(query: string, options: UiBlockSearchOptions = {}): readonly UiBlockSummary[] {
  const tokens = tokenize(query)
  const limit = Math.max(0, options.limit ?? 12)
  const discovery = discover({ category: options.category })

  if (tokens.length === 0) return discovery.blocks.slice(0, limit)

  return discovery.blocks
    .map((block) => ({ block, score: scoreBlock(block, tokens) }))
    .filter(({ score }) => score > 0)
    .sort((left, right) => right.score - left.score || left.block.id.localeCompare(right.block.id))
    .slice(0, limit)
    .map(({ block }) => block)
}

/** Returns at most three deterministic recommendations grounded in catalog metadata. */
export function recommend({
  intent,
  limit = 3,
}: UiBlockRecommendationRequest): readonly UiBlockSummary[] {
  return search(intent, { limit: Math.min(3, Math.max(0, limit)) })
}

/** Resolves a preview only after verifying that the exact ref is catalog-admitted. */
export async function resolvePreview(ref: UiBlockRef): Promise<ResolvedUiBlockPreview> {
  const summary = discover({ category: ref.category }).blocks.find(
    (block) => block.blockId === ref.blockId,
  )
  if (!summary) {
    throw new UiBlockPreviewAuthorityError('not-admitted', ref)
  }

  const block = await loadBlock(ref.category, ref.blockId)
  if (Object.keys(block.manifest).length === 0 || Object.keys(block.ui).length === 0) {
    throw new Error(`UI Block preview could not be resolved: ${ref.category}/${ref.blockId}`)
  }
  const designSystem = resolveBaselineDesignSystem(block, ref)

  return {
    ref,
    summary,
    block,
    designSystem,
    designOntologyRelease: {
      releaseId: BASELINE_DESIGN_ONTOLOGY_RELEASE.releaseId,
      contentHash: BASELINE_DESIGN_ONTOLOGY_RELEASE.contentHash,
    },
    mode: 'read-only',
  }
}

/**
 * Replacement authority is intentionally absent from this read-only adapter.
 * Returning no candidates prevents UI convenience from becoming publication authority.
 */
export function compatibleReplacements(_ref: UiBlockRef): UiBlockReplacementResult {
  return {
    available: false,
    candidates: [],
    reason: 'replacement-authority-unavailable',
    explanation:
      'Project replacement remains unavailable until structural compatibility and publication authority are proven.',
  }
}

function scoreBlock(block: UiBlockSummary, tokens: readonly string[]): number {
  const id = normalize(block.id)
  const category = normalize(block.category)
  const categoryLabel = normalize(block.categoryLabel)
  const description = normalize(block.description)
  const tags = block.tags.map(normalize)

  return tokens.reduce((score, token) => {
    if (id === token) score += 24
    else if (id.includes(token)) score += 12
    if (category === token) score += 18
    else if (category.includes(token)) score += 8
    if (categoryLabel.includes(token)) score += 7
    if (tags.includes(token)) score += 10
    else if (tags.some((tag) => tag.includes(token))) score += 5
    if (description.includes(token)) score += 3
    return score
  }, 0)
}
